import { useState, useEffect, useCallback } from 'react';
import { 
  Download, RefreshCw, Search, X, Eye, EyeOff, Loader2, 
  ExternalLink, Plus, Send, Link
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { Header } from '@/components';
import { loadCompanies } from '@/services/contacts';
import { getCredits, deductCredits } from '@/services/credits';
import { API_CONFIG, CREDIT_COSTS as GLOBAL_CREDIT_COSTS } from '@/services/api';
import { enqueueScrapeJob } from '@/services/scrapeQueue';
import { openDB, IDBPDatabase } from 'idb';
import { FEATURE_FLAGS } from '@/services/api';

// API Endpoints
const ECOM_ORCHESTRATOR_API = API_CONFIG.ECOM_ORCHESTRATOR_API;
const GRADES_API = API_CONFIG.GRADES_API;
const MAKE_WEBHOOK_URL = API_CONFIG.MAKE_WEBHOOK_URL;

// Credit costs (keep website/ecommerce as-is; use global costs for socials)
const CREDIT_COSTS: Record<string, number> = {
  website: 5,
  ecommerce: 4,
  linkedin: GLOBAL_CREDIT_COSTS.linkedin_profile_only ?? 1,
  facebook: GLOBAL_CREDIT_COSTS.facebook ?? 2,
  instagram: GLOBAL_CREDIT_COSTS.instagram ?? 2,
  tiktok: GLOBAL_CREDIT_COSTS.tiktok ?? 2,
  youtube: GLOBAL_CREDIT_COSTS.youtube ?? 2,
  x: GLOBAL_CREDIT_COSTS.x ?? 2,
  podcast: 2,
};

// Types
interface EcomCompany {
  companyId: string;
  companyName: string;
  website: string;
}

interface SocialLinks {
  linkedin?: string;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  x?: string;
  podcast?: string;
  twitter?: string;
}

interface EcomIntelEntry {
  data: unknown;
  scrapedAt?: string;
  socialLinks?: SocialLinks;
}

interface EcomIntel {
  website?: EcomIntelEntry;
  ecommerce?: EcomIntelEntry;
  linkedin?: EcomIntelEntry;
  facebook?: EcomIntelEntry;
  instagram?: EcomIntelEntry;
  tiktok?: EcomIntelEntry;
  youtube?: EcomIntelEntry;
  x?: EcomIntelEntry;
  podcast?: EcomIntelEntry;
  social_links?: SocialLinks;
  ecomiq?: EcomIntelEntry;
  marketingiq?: EcomIntelEntry;
  adreadyiq?: EcomIntelEntry;
  affiliateiq?: EcomIntelEntry;
  productiq?: EcomIntelEntry;
  ecommap?: EcomIntelEntry;
}

// IndexedDB setup
const DB_NAME = 'ProfitibullEcomIntel';
const DB_VERSION = 1;

async function getDB(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('companyIntel')) {
        db.createObjectStore('companyIntel', { keyPath: 'companyId' });
      }
    },
  });
}

async function saveIntelToDB(companyId: string, intel: EcomIntel) {
  const db = await getDB();
  await db.put('companyIntel', { companyId, intel });
}

async function loadAllIntelFromDB(): Promise<Record<string, EcomIntel>> {
  const db = await getDB();
  const all = await db.getAll('companyIntel');
  const data: Record<string, EcomIntel> = {};
  all.forEach((item: { companyId: string; intel: EcomIntel }) => {
    data[item.companyId] = item.intel;
  });
  return data;
}

const isMockItem = (item: unknown): boolean => {
  if (!item || typeof item !== 'object') return false;
  return (item as Record<string, unknown>).note === 'SOLO mock ecom output';
};

const isMockData = (data: unknown): boolean => {
  if (Array.isArray(data)) return data.some(isMockItem);
  if (data && typeof data === 'object') return isMockItem(data);
  return false;
};

const normalizeWebsiteUrl = (input: string): string => {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export function EcommerceIntelligence() {
  const { userEmail, credits, setCredits, isAuthenticated } = useAppStore();

  const [companies, setCompanies] = useState<EcomCompany[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<EcomCompany[]>([]);
  const [ecomIntelData, setEcomIntelData] = useState<Record<string, EcomIntel>>({});
  const [watchList, setWatchList] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, Record<string, boolean>>>({});
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  
  const [scrapeModal, setScrapeModal] = useState<{ type: string; company: string; progress: number; status: string } | null>(null);
  const [dataDrawer, setDataDrawer] = useState<{ companyId: string; companyName: string; type: string } | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyWebsite, setNewCompanyWebsite] = useState('');
  const [showRawDrawer, setShowRawDrawer] = useState(false);

  // Load companies from API (same as other intelligence pages)
  const loadData = useCallback(async (signal?: AbortSignal) => {
    if (!userEmail) return;
    
    setLoading(true);
    try {
      // Load credits
      const creds = await getCredits(userEmail);
      if (signal?.aborted) return;
      setCredits(creds);
      
      // Load intel from IndexedDB
      const savedIntel = await loadAllIntelFromDB();
      if (signal?.aborted) return;
      const cleanedIntel: Record<string, EcomIntel> = {};
      for (const [companyId, intel] of Object.entries(savedIntel)) {
        const next = { ...intel };
        let changed = false;
        (['website', 'ecommerce'] as Array<keyof EcomIntel>).forEach((source) => {
          const entry = next[source] as EcomIntelEntry | undefined;
          if (entry?.data && isMockData(entry.data)) {
            delete next[source];
            changed = true;
          }
        });
        cleanedIntel[companyId] = next;
        if (changed) await saveIntelToDB(companyId, next);
      }
      if (signal?.aborted) return;
      setEcomIntelData(cleanedIntel);
      
      // Load companies from API
      const companyList = await loadCompanies(userEmail, { signal });
      if (signal?.aborted) return;
      
      // Transform to EcomCompany format (extract website from company data)
      const ecomCompanies: EcomCompany[] = companyList.map(c => ({
        companyId: c.companyId,
        companyName: c.companyName,
        website: c.website || extractDomainFromLinkedIn(c.linkedin) || ''
      }));
      
      setCompanies(ecomCompanies);
      setFilteredCompanies(ecomCompanies);
      
      // Load watch list from localStorage
      const savedWatchList = localStorage.getItem('profitibull_ecom_watchlist');
      if (savedWatchList) setWatchList(JSON.parse(savedWatchList));
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') {
        console.error('Load failed:', e);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [userEmail, setCredits]);

  // Extract domain from LinkedIn URL
  const extractDomainFromLinkedIn = (linkedinUrl?: string): string => {
    if (!linkedinUrl) return '';
    // Try to extract company name from LinkedIn URL for domain guess
    const match = linkedinUrl.match(/linkedin\.com\/company\/([^\/]+)/);
    if (match) {
      return match[1].replace(/-/g, '') + '.com';
    }
    return '';
  };

  // Initial load
  useEffect(() => {
    if (isAuthenticated && userEmail) {
      const controller = new AbortController();
      loadData(controller.signal);
      return () => controller.abort();
    }
  }, [isAuthenticated, userEmail, loadData]);

  const summarizeWebsiteData = (data: unknown): {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    domains?: string[];
    phones?: string[];
    address?: string;
    socials?: Record<string, string[]>;
    keywords?: string[];
    sample?: string;
    pagesCount: number;
  } => {
    const pages = Array.isArray(data) ? data : [];
    const first = pages[0] as any;
    const meta = first?.metadata || {};
    const jsonLd = Array.isArray(meta?.jsonLd) ? meta.jsonLd : [];
    const graph = jsonLd
      .flatMap((node: any) => (node && node['@graph'] ? node['@graph'] : node))
      .filter(Boolean);
    const org =
      graph.find(
        (n: any) =>
          n && (n['@type'] === 'Organization' || n['@type'] === 'Corporation')
      ) || {};
    const canonicalUrl = meta?.canonicalUrl || org?.url || first?.url || '';
    const description = meta?.description || org?.description || '';
    const title = meta?.title || org?.name || '';
    const address = org?.address
      ? [
          org.address?.streetAddress,
          org.address?.addressLocality,
          org.address?.addressRegion,
          org.address?.postalCode,
          org.address?.addressCountry,
        ]
          .filter(Boolean)
          .join(', ')
      : '';
    const contactPoints = Array.isArray(org?.contactPoint)
      ? org.contactPoint
      : [];
    const phones = contactPoints
      .flatMap((cp: any) =>
        Array.isArray(cp?.telephone)
          ? cp.telephone
          : cp?.telephone
            ? [cp.telephone]
            : []
      )
      .filter(Boolean);
    const sameAs = Array.isArray(org?.sameAs) ? org.sameAs : [];
    const socials: Record<string, string[]> = {
      linkedin: [],
      facebook: [],
      instagram: [],
      tiktok: [],
      youtube: [],
      x: [],
      podcast: [],
    };
    for (const url of sameAs) {
      if (typeof url !== 'string') continue;
      if (url.includes('linkedin.com')) socials.linkedin.push(url);
      else if (url.includes('facebook.com')) socials.facebook.push(url);
      else if (url.includes('instagram.com')) socials.instagram.push(url);
      else if (url.includes('tiktok.com')) socials.tiktok.push(url);
      else if (url.includes('youtube.com') || url.includes('youtu.be'))
        socials.youtube.push(url);
      else if (url.includes('x.com') || url.includes('twitter.com'))
        socials.x.push(url);
      else if (
        url.includes('podcast') ||
        url.includes('podbean') ||
        url.includes('spotify') ||
        url.includes('apple.com')
      )
        socials.podcast.push(url);
    }
    const keywords = Array.isArray(meta?.keywords)
      ? meta.keywords
      : typeof meta?.keywords === 'string'
        ? meta.keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
        : [];
    const sample = typeof first?.text === 'string' ? first.text.slice(0, 420) : '';
    const domains = canonicalUrl ? [canonicalUrl] : [];
    return {
      title,
      description,
      canonicalUrl,
      domains,
      phones,
      address,
      socials,
      keywords,
      sample,
      pagesCount: pages.length,
    };
  };
  useEffect(() => {
    setShowRawDrawer(false);
  }, [dataDrawer?.companyId, dataDrawer?.type]);

  // Filter companies
  useEffect(() => {
    let filtered = companies.filter(c => 
      c.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.website.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (filter === 'watched') {
      filtered = filtered.filter(c => watchList[c.companyId]);
    } else if (filter === 'graded') {
      filtered = filtered.filter(c => {
        const intel = ecomIntelData[c.companyId] || {};
        return intel.ecomiq || intel.marketingiq || intel.adreadyiq || intel.affiliateiq || intel.productiq || intel.ecommap;
      });
    } else if (filter === 'scraped') {
      filtered = filtered.filter(c => {
        const intel = ecomIntelData[c.companyId] || {};
        return intel.website || intel.ecommerce || intel.linkedin || intel.facebook || intel.instagram || intel.tiktok || intel.youtube || intel.x || intel.podcast;
      });
    }
    
    setFilteredCompanies(filtered);
    setCurrentPage(1);
  }, [companies, searchTerm, filter, watchList, ecomIntelData]);

  const setLoadingState = (companyId: string, action: string, isLoading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [companyId]: { ...prev[companyId], [action]: isLoading }
    }));
  };

  const summarizeEcommerceData = (data: unknown): {
    total: number;
    withPrice: number;
    currency?: string;
    minPrice?: number;
    maxPrice?: number;
    avgPrice?: number;
    domains: string[];
    samples: Array<{
      title: string;
      price?: string;
      url?: string;
      image?: string;
      rating?: string;
      reviews?: string;
      availability?: string;
    }>;
  } => {
    const rawItems = Array.isArray(data)
      ? data
      : Array.isArray((data as { items?: unknown[] })?.items)
        ? (data as { items?: unknown[] }).items ?? []
        : [];
    const items = rawItems.map((entry: any) => (entry && typeof entry === 'object' && entry.items ? entry.items : entry));
    const pick = (obj: any, keys: string[]) => {
      for (const key of keys) {
        const val = obj?.[key];
        if (val !== undefined && val !== null && val !== '') return val;
      }
      return undefined;
    };
    const toStr = (val: any) =>
      typeof val === 'string' ? val : typeof val === 'number' ? String(val) : '';
    const toNum = (val: any) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^0-9.]+/g, '');
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : undefined;
      }
      return undefined;
    };

    const prices: number[] = [];
    const domains = new Set<string>();
    const samples: Array<{
      title: string;
      price?: string;
      url?: string;
      image?: string;
      rating?: string;
      reviews?: string;
      availability?: string;
    }> = [];

    for (const raw of items) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, any>;
      const title =
        toStr(pick(item, ['product_title', 'title', 'name', 'productTitle', 'productName'])) ||
        'Untitled item';
      const url = toStr(pick(item, ['url', 'productUrl', 'itemUrl', 'detailUrl', 'detail_url']));
      const image = toStr(pick(item, ['image_url', 'image', 'imageUrl', 'thumbnail', 'thumb', 'img']));
      const priceVal = pick(item, ['product_price', 'price', 'priceValue', 'salePrice', 'sale_price', 'price_amount']);
      const priceNum = toNum(priceVal);
      if (priceNum !== undefined) prices.push(priceNum);
      const price =
        priceNum !== undefined
          ? priceNum.toFixed(2)
          : toStr(priceVal);
      const rating = toStr(pick(item, ['ratings', 'rating', 'ratingValue', 'reviewRating']));
      const reviews = toStr(pick(item, ['reviewCount', 'reviews', 'ratingsCount']));
      const availability = toStr(pick(item, ['availability', 'stockStatus', 'inStock']));

      if (url) {
        try {
          const u = new URL(url.startsWith('http') ? url : `https://${url}`);
          domains.add(u.hostname.replace(/^www\./, ''));
        } catch {
          // ignore
        }
      }

      if (samples.length < 8) {
        samples.push({
          title,
          price: price || undefined,
          url: url || undefined,
          image: image || undefined,
          rating: rating || undefined,
          reviews: reviews || undefined,
          availability: availability || undefined,
        });
      }
    }

    const minPrice = prices.length ? Math.min(...prices) : undefined;
    const maxPrice = prices.length ? Math.max(...prices) : undefined;
    const avgPrice = prices.length
      ? prices.reduce((sum, v) => sum + v, 0) / prices.length
      : undefined;
    const currency = undefined;

    return {
      total: items.length,
      withPrice: prices.length,
      currency,
      minPrice,
      maxPrice,
      avgPrice,
      domains: Array.from(domains).slice(0, 5),
      samples,
    };
  };

  const summarizeLinkedinData = (data: unknown): {
    name?: string;
    description?: string;
    industry?: string;
    website?: string;
    headquarters?: string;
    employeeCount?: number;
    size?: string;
    followers?: string | number;
    logo?: string;
    posts: Array<{
      date?: string;
      text?: string;
      url?: string;
      likes?: number | string;
      comments?: number | string;
      shares?: number | string;
      views?: number | string;
    }>;
    specialties: string[];
    similar: Array<{ name?: string; link?: string }>;
    employees: Array<{ name?: string; link?: string; image?: string }>;
  } => {
    const items = Array.isArray(data) ? data : [];
    const first = (items[0] as Record<string, any>) || {};
    const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
    const name = getStr(first.name || first.companyName || first.title);
    const description = getStr(first.description || first.about);
    const industry = getStr(first.industry);
    const website = getStr(first.website || first.domain);
    const headquarters = getStr(first.headquarters || first.location?.city);
    const employeeCount = typeof first.employeeCount === 'number' ? first.employeeCount : undefined;
    const size = getStr(first.size);
    const followers = first.followers ?? first.followersCount ?? undefined;
    const logo = getStr(first.logo || first.logoUrl);
    const posts = Array.isArray(first.posts)
      ? first.posts.slice(0, 8).map((p: any) => ({
          date: getStr(p.datePublished || p.date),
          text: getStr(p.text || p.caption),
          url: getStr(p.url || p.postUrl),
          likes: p.likes ?? p.likeCount ?? p.reactions ?? p.reactionCount ?? undefined,
          comments: p.comments ?? p.commentCount ?? undefined,
          shares: p.shares ?? p.shareCount ?? undefined,
          views: p.views ?? p.viewCount ?? undefined,
        }))
      : [];
    const specialties = Array.isArray(first.specialties) ? first.specialties.slice(0, 10) : [];
    const similar = Array.isArray(first.similarPages)
      ? first.similarPages.slice(0, 8).map((s: any) => ({
          name: getStr(s.name),
          link: getStr(s.link || s.url),
        }))
      : [];
    const employees = Array.isArray(first.employees)
      ? first.employees.slice(0, 8).map((e: any) => ({
          name: getStr(e.name),
          link: getStr(e.link || e.url),
          image: getStr(e.image || e.avatar),
        }))
      : [];
    return {
      name,
      description,
      industry,
      website,
      headquarters,
      employeeCount,
      size,
      followers,
      logo,
      posts,
      specialties,
      similar,
      employees,
    };
  };

  const summarizeFacebookData = (data: unknown): {
    title?: string;
    pageUrl?: string;
    likes?: number | string;
    followers?: number | string;
    category?: string;
    phone?: string;
    website?: string;
    address?: string;
    intro?: string;
    profilePictureUrl?: string;
    coverPhotoUrl?: string;
    posts: Array<{
      date?: string;
      text?: string;
      url?: string;
      likes?: number | string;
      comments?: number | string;
      shares?: number | string;
      views?: number | string;
      image?: string;
      video?: string;
    }>;
  } => {
    const payload = (data && typeof data === 'object') ? (data as Record<string, any>) : {};
    const items = Array.isArray(data)
      ? data
      : Array.isArray(payload.items)
        ? payload.items
        : [];
    const pageArray = Array.isArray(payload.page) ? payload.page : [];
    const postsArray = Array.isArray(payload.posts) ? payload.posts : [];
    const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
    const pageItem =
      pageArray[0] ||
      items.find((i: any) => i && (i.pageName || i.pageUrl || i.title || i.facebookUrl));
    const postItems =
      postsArray.length > 0
        ? postsArray
        : items.filter((i: any) => i && (i.post_id || i.post_url || i.text));
    const page = (pageItem as Record<string, any>) || {};

    const title = getStr(page.pageName || page.title || page.facebookUrl);
    const pageUrl = getStr(page.pageUrl || page.facebookUrl);
    const likes = page.likes ?? undefined;
    const followers = page.followers ?? undefined;
    const category = getStr(page.category || (Array.isArray(page.categories) ? page.categories.join(', ') : ''));
    const phone = getStr(page.phone);
    const website = getStr(page.website);
    const address = getStr(page.address);
    const intro = getStr(page.intro);
    const profilePictureUrl = getStr(page.profilePictureUrl);
    const coverPhotoUrl = getStr(page.coverPhotoUrl);

    const posts = postItems.slice(0, 10).map((p: any) => ({
      date: p.create_time ? new Date(p.create_time).toISOString() : '',
      text: getStr(p.text),
      url: getStr(p.post_url || p.postUrl || p.input),
      likes: p.like_count ?? p.likes ?? undefined,
      comments: p.comment_count ?? p.comments ?? undefined,
      shares: p.share_count ?? p.shares ?? undefined,
      views: p.view_count ?? p.play_count ?? undefined,
      image: Array.isArray(p.image_list) ? p.image_list[0] : undefined,
      video: Array.isArray(p.video_list) ? p.video_list[0] : undefined,
    }));

    return {
      title,
      pageUrl,
      likes,
      followers,
      category,
      phone,
      website,
      address,
      intro,
      profilePictureUrl,
      coverPhotoUrl,
      posts,
    };
  };

  const summarizeInstagramData = (data: unknown): {
    username?: string;
    fullName?: string;
    bio?: string;
    website?: string;
    followers?: number | string;
    following?: number | string;
    postsCount?: number | string;
    profileUrl?: string;
    avatar?: string;
    isVerified?: boolean;
    recentPosts: Array<{
      caption?: string;
      url?: string;
      likes?: number | string;
      comments?: number | string;
      date?: string;
      image?: string;
      video?: string;
      views?: number | string;
    }>;
  } => {
    const payload = data && typeof data === 'object' ? (data as Record<string, any>) : undefined;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(payload?.results)
        ? payload?.results
        : Array.isArray(payload?.items)
          ? payload?.items
          : data && typeof data === 'object'
            ? [data]
            : [];
    const getStr = (v: any) =>
      typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
    const first = (items[0] as Record<string, any>) || {};

    // Instagram payload is usually a profile object with latestPosts
    const profile = first;

    const username = getStr(profile.username || profile.userName || profile.handle);
    const fullName = getStr(profile.fullName || profile.name || profile.nickName);
    const bio = getStr(profile.biography || profile.bio);
    const website = getStr(profile.externalUrl || profile.externalUrlShimmed || profile.website);
    const followers = profile.followersCount ?? profile.followers ?? undefined;
    const following = profile.followsCount ?? profile.following ?? undefined;
    const postsCount = profile.postsCount ?? profile.posts ?? profile.igtvVideoCount ?? undefined;
    const profileUrl = getStr(profile.url || profile._discovered_profile_url || profile.inputUrl);
    const avatar = getStr(profile.profilePicUrlHD || profile.profilePicUrl || profile.avatar || profile.profilePicture);
    const isVerified = Boolean(profile.verified || profile.isVerified);

    const postSource = Array.isArray(profile.latestPosts) && profile.latestPosts.length > 0
      ? profile.latestPosts
      : Array.isArray(profile.latestIgtvVideos) && profile.latestIgtvVideos.length > 0
        ? profile.latestIgtvVideos
        : [];
    const recentPosts = postSource.slice(0, 8).map((p: any) => ({
      caption: getStr(p.caption || p.text || p.description),
      url: getStr(p.url || p.postUrl),
      likes: p.likesCount ?? p.likes ?? p.likeCount ?? undefined,
      comments: p.commentsCount ?? p.comments ?? p.commentCount ?? undefined,
      views: p.videoViewCount ?? undefined,
      date: getStr(p.timestamp || p.date),
      image: Array.isArray(p.images) ? p.images[0] : p.displayUrl || p.imageUrl || p.thumbnail,
      video: p.videoUrl || p.video,
    }));

    return {
      username,
      fullName,
      bio,
      website,
      followers,
      following,
      postsCount,
      profileUrl,
      avatar,
      isVerified,
      recentPosts,
    };
  };

  const summarizeTikTokData = (data: unknown): {
    username?: string;
    fullName?: string;
    bio?: string;
    website?: string;
    followers?: number | string;
    following?: number | string;
    videos?: number | string;
    hearts?: number | string;
    profileUrl?: string;
    avatar?: string;
    isVerified?: boolean;
    recentPosts: Array<{
      caption?: string;
      url?: string;
      likes?: number | string;
      comments?: number | string;
      shares?: number | string;
      views?: number | string;
      date?: string;
      image?: string;
      duration?: number | string;
      hashtags?: string[];
    }>;
  } => {
    const items = Array.isArray(data) ? data : [];
    const getStr = (v: any) =>
      typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
    const first = (items[0] as Record<string, any>) || {};
    const meta = first.authorMeta || {};

    const username = getStr(meta.name || meta.username);
    const fullName = getStr(meta.nickName || meta.name);
    const bio = getStr(meta.signature);
    const website = getStr(meta.bioLink);
    const followers = meta.fans ?? undefined;
    const following = meta.following ?? undefined;
    const videos = meta.video ?? undefined;
    const hearts = meta.heart ?? undefined;
    const profileUrl = getStr(meta.profileUrl || first._discovered_profile_url);
    const avatar = getStr(meta.originalAvatarUrl || meta.avatar);
    const isVerified = Boolean(meta.verified);

    const recentPosts = items.slice(0, 8).map((p: any) => ({
      caption: getStr(p.text),
      url: getStr(p.webVideoUrl),
      likes: p.diggCount ?? undefined,
      comments: p.commentCount ?? undefined,
      shares: p.shareCount ?? undefined,
      views: p.playCount ?? undefined,
      date: getStr(p.createTimeISO),
      image: p.videoMeta?.coverUrl || p.videoMeta?.originalCoverUrl,
      duration: p.videoMeta?.duration ?? undefined,
      hashtags: Array.isArray(p.hashtags) ? p.hashtags.map((h: any) => getStr(h.name)).filter(Boolean) : [],
    }));

    return {
      username,
      fullName,
      bio,
      website,
      followers,
      following,
      videos,
      hearts,
      profileUrl,
      avatar,
      isVerified,
      recentPosts,
    };
  };

  const summarizeYouTubeData = (data: unknown): {
    channelName?: string;
    channelUrl?: string;
    channelAvatar?: string;
    subscribers?: number | string;
    totalViews?: number | string;
    totalVideos?: number | string;
    channelLocation?: string;
    recentVideos: Array<{
      title?: string;
      url?: string;
      date?: string;
      views?: number | string;
      likes?: number | string;
      comments?: number | string;
      thumbnail?: string;
      text?: string;
    }>;
  } => {
    const items = Array.isArray(data) ? data : [];
    const cleaned = items.filter((item: any) => {
      const note = String(item?.note || '').toLowerCase();
      const error = String(item?.error || '');
      return error !== 'NO_RESULTS' && !note.includes('no results were collected');
    });
    const first = (cleaned[0] as Record<string, any>) || {};
    const about = first.aboutChannelInfo || {};
    const channelName = about.channelName || first.channelName;
    const channelUrl = about.channelUrl || first.channelUrl;
    const channelAvatar = about.channelAvatarUrl || first.channelAvatarUrl;
    const subscribers = about.numberOfSubscribers || first.numberOfSubscribers;
    const totalViews = about.channelTotalViews || first.channelTotalViews;
    const totalVideos = about.channelTotalVideos || first.channelTotalVideos;
    const channelLocation = about.channelLocation || first.channelLocation;
    const recentVideos = cleaned.slice(0, 8).map((v: any) => ({
      title: v.title,
      url: v.url,
      date: v.date,
      views: v.viewCount,
      likes: v.likes,
      comments: v.commentsCount,
      thumbnail: v.thumbnailUrl,
      text: v.text,
    }));
    return {
      channelName,
      channelUrl,
      channelAvatar,
      subscribers,
      totalViews,
      totalVideos,
      channelLocation,
      recentVideos,
    };
  };

  const stripHtml = (input?: string) => {
    if (!input) return '';
    return input.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  const summarizePodcastData = (data: unknown): {
    showTitle?: string;
    publisher?: string;
    description?: string;
    website?: string;
    rssUrl?: string;
    image?: string;
    latestEpisode?: {
      title?: string;
      duration?: string;
      episodeUrl?: string;
      description?: string;
    };
    episodes: Array<{
      title?: string;
      duration?: string;
      episodeUrl?: string;
      description?: string;
    }>;
  } => {
    const items = Array.isArray(data) ? data : [];
    const cleaned = items.filter((item) => !(item && typeof item === 'object' && 'warning' in (item as Record<string, unknown>)));
    const first = (cleaned[0] as Record<string, any>) || {};
    const showTitle = first.showTitle;
    const publisher = first.publisher;
    const description = first.description;
    const website = first.website;
    const rssUrl = first.rssUrl;
    const image = first.image;
    const latestEpisode = first.latestEpisode;
    const episodes = Array.isArray(first.episodes) ? first.episodes : [];
    return {
      showTitle,
      publisher,
      description,
      website,
      rssUrl,
      image,
      latestEpisode,
      episodes,
    };
  };

  const summarizeXData = (data: unknown): {
    profileName?: string;
    profileHandle?: string;
    profileUrl?: string;
    avatar?: string;
    bio?: string;
    followers?: number | string;
    following?: number | string;
    recentPosts: Array<{
      text?: string;
      url?: string;
      date?: string;
      likes?: number | string;
      replies?: number | string;
      reposts?: number | string;
      quotes?: number | string;
      views?: number | string;
      media?: string;
      repliesList?: Array<Record<string, any>>;
    }>;
  } => {
    const raw = data as Record<string, any> | undefined;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(raw?.results)
        ? raw?.results
        : Array.isArray(raw?.data)
          ? raw?.data
          : Array.isArray(raw?.items)
            ? raw?.items
            : [];
    const first = (items[0] as Record<string, any>) || {};
    const author = first.author || first.user || {};
    const profileName = author.name || first.authorName || first.userName;
    const profileHandle = author.screenName || author.userName || author.username || first.authorUsername;
    const profileUrl = first._discovered_profile_url || author.profileUrl || first.authorProfileUrl;
    const avatar = author.profileImageUrl || author.profileImageUrlHttps || first.authorProfileImageUrl || first.profileImageUrl;
    const bio = author.description || first.authorDescription;
    const followers = author.followersCount || author.followers || undefined;
    const following = author.friendsCount || author.following || undefined;
    const recentPosts = items.slice(0, 10).map((p: any) => {
      const repliesList = Array.isArray(p.replies)
        ? p.replies
        : Array.isArray(p.comments)
          ? p.comments
          : Array.isArray(p.replies?.items)
            ? p.replies.items
            : undefined;
      return {
        text: p.postText || p.fullText || p.text,
        url: p.postUrl || p.url || p.tweetUrl,
        date: p.createdAt || p.date || p.timestamp,
        likes: p.favouriteCount || p.favoriteCount || p.likeCount,
        replies: p.replyCount,
        reposts: p.repostCount || p.retweetCount,
        quotes: p.quoteCount,
        views: p.viewCount || p.impressionCount,
        media: Array.isArray(p.media) && p.media[0] ? (p.media[0].mediaUrlHttps || p.media[0].mediaUrl) : undefined,
        repliesList,
      };
    });
    return {
      profileName,
      profileHandle,
      profileUrl,
      avatar,
      bio,
      followers,
      following,
      recentPosts,
    };
  };

  const isActionLoading = (companyId: string, action: string) => {
    return loadingStates[companyId]?.[action] || false;
  };

  const saveEcomIntel = async (companyId: string, source: string, data: unknown, socialLinks?: SocialLinks) => {
    const newIntel = { ...ecomIntelData };
    if (!newIntel[companyId]) newIntel[companyId] = {};
    newIntel[companyId][source as keyof EcomIntel] = { 
      data, 
      scrapedAt: new Date().toISOString(),
      ...(socialLinks && { socialLinks })
    } as EcomIntelEntry;
    setEcomIntelData(newIntel);
    await saveIntelToDB(companyId, newIntel[companyId]);
  };

  const clearIntelSource = async (companyId: string, source: keyof EcomIntel) => {
    const newIntel = { ...ecomIntelData };
    if (!newIntel[companyId]) return;
    delete newIntel[companyId][source];
    setEcomIntelData(newIntel);
    await saveIntelToDB(companyId, newIntel[companyId]);
  };

  const pollForResults = async (runId: string, datasetId?: string): Promise<unknown> => {
    const maxAttempts = 90;
    const delayMs = 3000;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, delayMs));
      const progress = 30 + Math.floor((i / maxAttempts) * 50);
      setScrapeModal(prev => prev ? { ...prev, progress, status: `Checking status (${i + 1}/${maxAttempts})...` } : null);
      try {
        const statusRes = await fetch(`${ECOM_ORCHESTRATOR_API}/check_status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ run_id: runId, dataset_id: datasetId })
        });
        const statusData = await statusRes.json();
        if (statusData.status === 'SUCCEEDED') {
          setScrapeModal(prev => prev ? { ...prev, progress: 90, status: 'Fetching results...' } : null);
          const resultsRes = await fetch(`${ECOM_ORCHESTRATOR_API}/get_results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dataset_id: datasetId || statusData.dataset_id })
          });
          const resultsData = await resultsRes.json();
          return resultsData.results;
        } else if ((statusData.status === 'FAILED' || statusData.status === 'ABORTED') && (datasetId || statusData.dataset_id)) {
          const resultsRes = await fetch(`${ECOM_ORCHESTRATOR_API}/get_results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dataset_id: datasetId || statusData.dataset_id })
          });
          const resultsData = await resultsRes.json();
          if (Array.isArray(resultsData.results) && resultsData.results.length > 0) {
            return resultsData.results;
          }
          continue;
        } else if (statusData.status === 'FAILED' || statusData.status === 'ABORTED') {
          throw new Error('Scrape failed');
        }
      } catch (e) {
        console.error('Poll error:', e);
      }
    }
    return null;
  };

  const refreshCredits = async () => {
    if (!userEmail) return;
    try {
      const creds = await getCredits(userEmail);
      setCredits(creds);
    } catch {
      // ignore
    }
  };

  const extractSocialLinksFromWebsite = (websiteData: unknown[]): SocialLinks => {
    const links: SocialLinks = {};
    for (const page of websiteData) {
      const p = page as { metadata?: { jsonLd?: Array<{ sameAs?: string[] }> }; text?: string; markdown?: string; html?: string };
      const jsonLd = p.metadata?.jsonLd || [];
      for (const ld of jsonLd) {
        if (ld.sameAs && Array.isArray(ld.sameAs)) {
          for (const url of ld.sameAs) {
            if (url.includes('linkedin.com') && !links.linkedin) links.linkedin = url;
            if (url.includes('facebook.com') && !links.facebook) links.facebook = url;
            if (url.includes('instagram.com') && !links.instagram) links.instagram = url;
            if (url.includes('tiktok.com') && !links.tiktok) links.tiktok = url;
            if (url.includes('youtube.com') && !links.youtube) links.youtube = url;
            if ((url.includes('x.com') || url.includes('twitter.com')) && !links.x) links.x = url;
          }
        }
      }
      const text = (p.text || '') + ' ' + (p.markdown || '') + ' ' + (p.html || '');
      if (!links.linkedin) { const match = text.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(?:company|in)\/[\w.-]+/i); if (match) links.linkedin = match[0].startsWith('http') ? match[0] : 'https://' + match[0]; }
      if (!links.facebook) { const match = text.match(/(?:https?:\/\/)?(?:www\.)?facebook\.com\/[\w.-]+/i); if (match) links.facebook = match[0].startsWith('http') ? match[0] : 'https://' + match[0]; }
      if (!links.instagram) { const match = text.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/[\w.-]+/i); if (match) links.instagram = match[0].startsWith('http') ? match[0] : 'https://' + match[0]; }
      if (!links.tiktok) { const match = text.match(/(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@[\w.-]+/i); if (match) links.tiktok = match[0].startsWith('http') ? match[0] : 'https://' + match[0]; }
      if (!links.youtube) { const match = text.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/(?:c\/|channel\/|@|user\/)[\w.-]+/i); if (match) links.youtube = match[0].startsWith('http') ? match[0] : 'https://' + match[0]; }
      if (!links.x) { const match = text.match(/(?:https?:\/\/)?(?:www\.)?(?:x|twitter)\.com\/[\w.-]+/i); if (match) links.x = match[0].startsWith('http') ? match[0] : 'https://' + match[0]; }
    }
    return links;
  };

  // Scrape handlers
  const handleScrapeWebsite = async (company: EcomCompany, options?: { forceRefresh?: boolean }) => {
    if (credits < CREDIT_COSTS.website) { alert('Insufficient credits'); return; }
    const currentIntel = ecomIntelData[company.companyId] || {};
    const websiteUrl = normalizeWebsiteUrl(company.website);
    if (!websiteUrl) { alert('Website required'); return; }
    if (options?.forceRefresh) await clearIntelSource(company.companyId, 'website');
    setLoadingState(company.companyId, 'website', true);
    setScrapeModal({ type: 'Website', company: company.companyName, progress: 10, status: 'Starting website scrape...' });
    try {
      if (!FEATURE_FLAGS.SERVER_SIDE_CREDITS) {
        await deductCredits(userEmail, CREDIT_COSTS.website, 'ecom_website');
        setCredits(credits - CREDIT_COSTS.website);
      }
      setScrapeModal(prev => prev ? { ...prev, progress: 20, status: 'Queued. Running in background...' } : null);
      setTimeout(() => setScrapeModal(null), 800);

      enqueueScrapeJob(
        {
          source: 'website',
          companyName: company.companyName,
          label: `${company.companyName} • website`,
        },
        async (update) => {
          update(10, 'Starting website scrape...');
          const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/website`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              website_url: websiteUrl,
              company_name: company.companyName,
              userEmail,
              force_refresh: Boolean(options?.forceRefresh),
            })
          });
          const data = await res.json();
          if (!data.success || !data.content?.run_id) {
            throw new Error(data.error || 'Failed to start website scrape');
          }
          update(30, 'Crawling website...');
          const results = await pollForResults(data.content.run_id, data.content.dataset_id);
          if (results && Array.isArray(results) && results.length > 0) {
            const socialLinks = extractSocialLinksFromWebsite(results);
            await saveEcomIntel(company.companyId, 'website', results, socialLinks);
            const discoveredCount = Object.values(socialLinks || {}).reduce(
              (sum, v) => sum + (Array.isArray(v) ? v.length : 0),
              0
            );
            if (discoveredCount > 0 && (currentIntel.marketingiq || currentIntel.ecommap)) {
              await handleGrade(company, 'marketingiq', { website: results, socialLinks });
              await handleGrade(company, 'ecommap', { website: results, socialLinks });
            }
          }

          await refreshCredits();
          setLoadingState(company.companyId, 'website', false);
        }
      );
      return;
    } catch (e) {
      console.error('Website scrape failed:', e);
      alert('Scrape failed');
    }
    await refreshCredits();
    setLoadingState(company.companyId, 'website', false);
    setScrapeModal(null);
  };

  const handleScrapeEcommerce = async (company: EcomCompany, options?: { forceRefresh?: boolean }) => {
    if (credits < CREDIT_COSTS.ecommerce) { alert('Insufficient credits'); return; }
    const websiteUrl = normalizeWebsiteUrl(company.website);
    if (!websiteUrl) { alert('Website required'); return; }
    if (options?.forceRefresh) await clearIntelSource(company.companyId, 'ecommerce');
    setLoadingState(company.companyId, 'ecommerce', true);
    setScrapeModal({ type: 'Products', company: company.companyName, progress: 10, status: 'Starting product scrape...' });
    try {
      if (!FEATURE_FLAGS.SERVER_SIDE_CREDITS) {
        await deductCredits(userEmail, CREDIT_COSTS.ecommerce, 'ecom_products');
        setCredits(credits - CREDIT_COSTS.ecommerce);
      }
      setScrapeModal(prev => prev ? { ...prev, progress: 20, status: 'Queued. Running in background...' } : null);
      setTimeout(() => setScrapeModal(null), 800);

      enqueueScrapeJob(
        {
          source: 'ecommerce',
          companyName: company.companyName,
          label: `${company.companyName} • products`,
        },
        async (update) => {
          update(10, 'Starting product scrape...');
          const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/ecommerce`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              website_url: websiteUrl,
              userEmail,
              force_refresh: Boolean(options?.forceRefresh),
            })
          });
          const data = await res.json();
          if (!data.success || !data.run_id) {
            throw new Error(data.error || 'Failed to start product scrape');
          }
          update(30, 'Scraping products...');
          const results = await pollForResults(data.run_id, data.dataset_id);
          if (results) {
            await saveEcomIntel(company.companyId, 'ecommerce', results);
          }

          await refreshCredits();
          setLoadingState(company.companyId, 'ecommerce', false);
        }
      );
      return;
    } catch (e) {
      console.error('Ecommerce scrape failed:', e);
      alert('Scrape failed');
    }
    await refreshCredits();
    setLoadingState(company.companyId, 'ecommerce', false);
    setScrapeModal(null);
  };

  const handleScrapeSocial = async (
    company: EcomCompany,
    platform: string,
    identifier: string | string[],
    options?: { skipCredit?: boolean; forceRefresh?: boolean }
  ) => {
    if (credits < CREDIT_COSTS[platform]) { alert('Insufficient credits'); return; }
    setLoadingState(company.companyId, platform, true);
    setScrapeModal({ type: platform, company: company.companyName, progress: 10, status: `Starting ${platform} scrape...` });
    try {
      if (!FEATURE_FLAGS.SERVER_SIDE_CREDITS && !options?.skipCredit) {
        await deductCredits(userEmail, CREDIT_COSTS[platform], `ecom_${platform}`);
        setCredits(credits - CREDIT_COSTS[platform]);
      }
      
      const endpoints: Record<string, string> = {
        linkedin: '/scrape/linkedin',
        facebook: '/scrape/facebook',
        instagram: '/scrape/instagram',
        tiktok: '/scrape/tiktok',
        youtube: '/scrape/youtube',
        x: '/scrape/x',
        podcast: '/scrape/podcast'
      };
      
      const rawIdentifier = Array.isArray(identifier) ? (identifier[0] ?? '') : identifier;
      const rawId = rawIdentifier?.trim() || '';
      const isCompanyFallback = rawId.toLowerCase() === company.companyName.toLowerCase();
      const usableId = isCompanyFallback ? '' : rawId;

      const isPlatformUrl = (value: string) => {
        if (!value) return false;
        const v = value.toLowerCase();
        if (platform === 'linkedin') return v.includes('linkedin.com');
        if (platform === 'facebook') return v.includes('facebook.com');
        if (platform === 'instagram') return v.includes('instagram.com');
        if (platform === 'tiktok') return v.includes('tiktok.com');
        if (platform === 'youtube') return v.includes('youtube.com') || v.includes('youtu.be');
        if (platform === 'x') return v.includes('x.com') || v.includes('twitter.com');
        return false;
      };

      let cleanId = usableId;
      if (platform === 'instagram' && usableId) cleanId = usableId.replace(/.*instagram\.com\/@?/, '').replace(/\/$/, '').split('?')[0];
      else if (platform === 'tiktok' && usableId) cleanId = usableId.replace(/.*tiktok\.com\/@?/, '').replace(/\/$/, '').split('?')[0];
      else if (platform === 'x' && usableId) cleanId = usableId.replace(/.*(?:x|twitter)\.com\/@?/, '').replace(/\/$/, '').split('?')[0];
      
      const basePayload = {
        company_name: company.companyName,
        website_url: normalizeWebsiteUrl(company.website),
      };

      const payloads: Record<string, object> = {
        linkedin: {
          ...(isPlatformUrl(usableId) ? { profile_url: usableId } : {}),
          contact_name: company.companyName,
        },
        facebook: {
          ...(isPlatformUrl(usableId) ? { page_url: usableId } : {}),
        },
        instagram: {
          ...(cleanId && (!usableId || !isPlatformUrl(usableId) || usableId.startsWith('@') || !usableId.includes('.')) ? { username: cleanId } : {}),
          contact_name: company.companyName,
        },
        tiktok: {
          ...(isPlatformUrl(usableId) ? { profile_url: usableId } : {}),
          ...(cleanId ? { username: cleanId } : {}),
        },
        youtube: {
          ...(isPlatformUrl(usableId) ? { channel_url: usableId } : {}),
          contact_name: company.companyName,
        },
        x: {
          ...(isPlatformUrl(usableId) ? { profile_url: usableId } : {}),
          contact_name: company.companyName,
        },
        podcast: { search_query: company.companyName, contact_name: company.companyName }
      };
      
      const res = await fetch(`${ECOM_ORCHESTRATOR_API}${endpoints[platform]}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...basePayload, ...payloads[platform], userEmail, force_refresh: Boolean(options?.forceRefresh) })
      });
      const data = await res.json();
      if (!data.success && data.error_code === 'profile_not_found') {
        const manualValue = window.prompt(
          `Could not find ${platform} profile for ${company.companyName}. Enter URL or username:`
        );
        if (manualValue && manualValue.trim()) {
          await handleScrapeSocial(company, platform, manualValue.trim(), { skipCredit: true });
        }
        setLoadingState(company.companyId, platform, false);
        setScrapeModal(null);
        return;
      }
      if (data.success && data.run_id) {
        setScrapeModal(prev => prev ? { ...prev, progress: 20, status: `Queued. Running in background...` } : null);
        setTimeout(() => setScrapeModal(null), 800);

        enqueueScrapeJob(
          {
            source: platform,
            companyName: company.companyName,
            label: `${company.companyName} • ${platform}`,
          },
          async (update) => {
            update(30, `Scraping ${platform}...`);
            const results = await pollForResults(data.run_id, data.dataset_id);
            if (results) {
              if (platform === 'facebook' && data.page_dataset_id) {
                const pageRes = await fetch(`${ECOM_ORCHESTRATOR_API}/get_results`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ dataset_id: data.page_dataset_id })
                });
                const pageData = await pageRes.json();
                await saveEcomIntel(company.companyId, platform, { page: pageData.results || [], posts: results });
              } else {
                await saveEcomIntel(company.companyId, platform, results);
              }
            }

            await refreshCredits();
            setLoadingState(company.companyId, platform, false);
          }
        );
        return;
      } else if (!data.success) {
        alert(data.error || `Failed to scrape ${platform}`);
      }
    } catch (e) {
      console.error(`${platform} scrape failed:`, e);
      alert('Scrape failed');
    }
    await refreshCredits();
    setLoadingState(company.companyId, platform, false);
    setScrapeModal(null);
  };

  const handleGrade = async (
    company: EcomCompany,
    gradeType: string,
    overrides?: { website?: unknown; socialLinks?: SocialLinks }
  ) => {
    const intel = ecomIntelData[company.companyId] || {};
    setLoadingState(company.companyId, gradeType, true);
    try {
      const companyData = {
        company_name: company.companyName,
        website: overrides?.website ?? intel.website?.data,
        ecommerce: intel.ecommerce?.data,
        facebook: intel.facebook?.data,
        instagram: intel.instagram?.data,
        tiktok: intel.tiktok?.data,
        youtube: intel.youtube?.data,
        social_links:
          overrides?.socialLinks ??
          intel.website?.socialLinks ??
          intel.social_links ??
          null
      };
      
      const res = await fetch(`${GRADES_API}/${gradeType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyData, companyId: company.companyId })
      });
      const data = await res.json();
      if (data.success && data.result) {
        await saveEcomIntel(company.companyId, gradeType, data.result);
      }
    } catch (e) {
      console.error(`${gradeType} failed:`, e);
      alert('Grade failed');
    } finally {
      setLoadingState(company.companyId, gradeType, false);
    }
  };

  const handlePush = async (company: EcomCompany) => {
    const intel = ecomIntelData[company.companyId] || {};
    setLoadingState(company.companyId, 'push', true);
    try {
      const payload = {
        companyId: company.companyId,
        companyName: company.companyName,
        website: company.website,
        website_data: intel.website?.data || null,
        social_links: intel.website?.socialLinks || intel.social_links || null,
        ecommerce: intel.ecommerce?.data || null,
        facebook: intel.facebook?.data || null,
        instagram: intel.instagram?.data || null,
        tiktok: intel.tiktok?.data || null,
        youtube: intel.youtube?.data || null,
        ecomiq: intel.ecomiq?.data || null,
        marketingiq: intel.marketingiq?.data || null,
        adreadyiq: intel.adreadyiq?.data || null,
        affiliateiq: intel.affiliateiq?.data || null,
        productiq: intel.productiq?.data || null,
        ecommap: intel.ecommap?.data || null,
        source: 'ecommerce_intelligence',
        pushedAt: new Date().toISOString(),
        pushedBy: userEmail
      };
      
      const response = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Webhook failed');
    } catch (e) {
      console.error('Push failed:', e);
      alert('Push failed');
    } finally {
      setLoadingState(company.companyId, 'push', false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName || !newCompanyWebsite) { alert('Please fill in both fields'); return; }
    const website = newCompanyWebsite.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
    const newCompany: EcomCompany = {
      companyId: 'custom-' + Date.now(),
      companyName: newCompanyName,
      website
    };
    const newCompanies = [newCompany, ...companies];
    setCompanies(newCompanies);
    setFilteredCompanies(newCompanies);
    setAddModal(false);
    setNewCompanyName('');
    setNewCompanyWebsite('');
  };

  const toggleWatch = (companyId: string) => {
    const newWatchList = { ...watchList };
    if (newWatchList[companyId]) delete newWatchList[companyId];
    else newWatchList[companyId] = true;
    setWatchList(newWatchList);
    localStorage.setItem('profitibull_ecom_watchlist', JSON.stringify(newWatchList));
  };

  const handleExportCSV = () => {
    if (filteredCompanies.length === 0) { alert('No companies to export'); return; }
    const headers = ['Company', 'Website', 'EcomIQ', 'MarketingIQ', 'AdReadyIQ', 'AffiliateIQ', 'ProductIQ', 'EcomMap Best Opp'];
    const rows = filteredCompanies.map(company => {
      const intel = ecomIntelData[company.companyId] || {};
      const ecomiq = intel.ecomiq?.data as { overall_grade?: string } | undefined;
      const marketingiq = intel.marketingiq?.data as { overall_grade?: string } | undefined;
      const adreadyiq = intel.adreadyiq?.data as { overall_grade?: string } | undefined;
      const affiliateiq = intel.affiliateiq?.data as { overall_grade?: string } | undefined;
      const productiq = intel.productiq?.data as { total_products_found?: number } | undefined;
      const ecommap = intel.ecommap?.data as { best_opportunity?: string } | undefined;
      return [
        company.companyName,
        company.website,
        ecomiq?.overall_grade || '',
        marketingiq?.overall_grade || '',
        adreadyiq?.overall_grade || '',
        affiliateiq?.overall_grade || '',
        productiq?.total_products_found?.toString() || '',
        ecommap?.best_opportunity || ''
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c + '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ecommerce_intelligence_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getGradeColor = (grade?: string) => {
    if (!grade) return 'bg-gray-700';
    const g = grade.toUpperCase();
    if (g.startsWith('A')) return 'bg-green-600';
    if (g.startsWith('B')) return 'bg-lime-600';
    if (g.startsWith('C')) return 'bg-yellow-600';
    if (g.startsWith('D')) return 'bg-orange-600';
    return 'bg-red-600';
  };

  const getSocialCoverage = (intel: EcomIntel) => {
    const sources: Array<keyof EcomIntel> = ['linkedin', 'facebook', 'instagram', 'tiktok', 'youtube', 'x', 'podcast'];
    const found = sources.reduce((count, s) => {
      const entry = intel[s];
      if (!entry || typeof entry !== 'object' || !('data' in entry)) return count;
      const data = entry.data as unknown;
      if (Array.isArray(data)) return count + (data.length > 0 ? 1 : 0);
      return count + (data ? 1 : 0);
    }, 0);
    return { found, total: sources.length };
  };

  const getSocialGrade = (found: number): string => {
    if (found >= 7) return 'A+';
    if (found === 6) return 'A';
    if (found === 5) return 'B+';
    if (found === 4) return 'B';
    if (found === 3) return 'C';
    if (found === 2) return 'D';
    if (found === 1) return 'D-';
    return 'F';
  };

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / perPage);
  const startIdx = (currentPage - 1) * perPage;
  const endIdx = Math.min(startIdx + perPage, filteredCompanies.length);
  const paginatedCompanies = filteredCompanies.slice(startIdx, endIdx);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
          <a href="/login" className="text-red-500 hover:text-red-400">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="ecom-page min-h-screen bg-dark-bg">
      <Header />

      {/* Scrape Modal */}
      {scrapeModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <div className="ecom-scrape-modal bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 text-center">
            <div className="mb-4">
              <Loader2 className="w-16 h-16 mx-auto animate-spin text-indigo-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Scraping {scrapeModal.type}</h3>
            <p className="text-gray-400 mb-4">Processing {scrapeModal.company}...</p>
            <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
              <div className="bg-indigo-500 h-2 rounded-full transition-all duration-1000" style={{ width: `${scrapeModal.progress}%` }} />
            </div>
            <p className="text-sm text-gray-500">{scrapeModal.status}</p>
          </div>
        </div>
      )}

      {/* Data Drawer */}
      {dataDrawer && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setDataDrawer(null)} />
          <div className="ecom-drawer-panel fixed top-0 right-0 w-[600px] max-w-[95vw] h-full bg-card-dark border-l border-gray-700 z-50 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{dataDrawer.companyName}</h3>
                  <p className="text-sm text-gray-400">{dataDrawer.type} Data</p>
                </div>
                <button onClick={() => setDataDrawer(null)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {(() => {
                const entry = ecomIntelData[dataDrawer.companyId]?.[dataDrawer.type as keyof EcomIntel];
                const payload = entry && 'data' in entry ? entry.data : entry;
                if (dataDrawer.type === 'website' && payload) {
                  const summary = summarizeWebsiteData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-slate-900 to-gray-900 border border-gray-700 rounded-xl p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400">Website Overview</p>
                            <h4 className="text-lg font-semibold text-white">{summary.title || dataDrawer.companyName}</h4>
                            <p className="text-sm text-gray-400 mt-1">{summary.description || 'No meta description found.'}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-600 text-white">Pages {summary.pagesCount}</span>
                            {summary.canonicalUrl && (
                              <a className="text-xs text-indigo-300 hover:text-indigo-200" href={summary.canonicalUrl} target="_blank" rel="noreferrer">
                                {summary.canonicalUrl.replace(/^https?:\/\//, '')}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Contact</p>
                          <p className="text-sm text-gray-300">{summary.address || 'Address not found'}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {(summary.phones || []).length > 0 ? (
                              summary.phones!.slice(0, 3).map((phone) => (
                                <span key={phone} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">{phone}</span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No phone numbers found</span>
                            )}
                          </div>
                        </div>
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Keywords</p>
                          <div className="flex flex-wrap gap-2">
                            {(summary.keywords || []).length > 0 ? (
                              summary.keywords!.slice(0, 8).map((kw) => (
                                <span key={kw} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">{kw}</span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No keywords found</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Social Links</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          {Object.entries(summary.socials || {}).map(([key, links]) => (
                            <div key={key} className="bg-gray-800 rounded-lg p-2">
                              <p className="text-gray-400">{key.toUpperCase()}</p>
                              <p className="text-gray-200">{links.length ? links.length : 0}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-2">Content Snapshot</p>
                        <p className="text-sm text-gray-300 leading-relaxed">{summary.sample || 'No page text captured.'}</p>
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'ecommerce' && payload) {
                  const summary = summarizeEcommerceData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-emerald-900/40 to-gray-900 border border-emerald-700/40 rounded-xl p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-emerald-300/80">Products Overview</p>
                            <h4 className="text-lg font-semibold text-white">{dataDrawer.companyName}</h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.total > 0 ? `${summary.total} items captured` : 'No products found yet'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600 text-white">
                              Products {summary.total}
                            </span>
                            {summary.withPrice > 0 && (
                              <span className="text-xs text-emerald-200">
                                Prices for {summary.withPrice}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Price Range</p>
                          {summary.minPrice !== undefined ? (
                            <div className="space-y-1 text-sm text-gray-300">
                              <div>Min: {summary.minPrice.toFixed(2)}</div>
                              <div>Max: {summary.maxPrice?.toFixed(2)}</div>
                              {summary.avgPrice !== undefined && (
                                <div>Avg: {summary.avgPrice.toFixed(2)}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">No prices detected</span>
                          )}
                        </div>
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Domains</p>
                          <div className="flex flex-wrap gap-2">
                            {summary.domains.length ? (
                              summary.domains.map((d) => (
                                <span key={d} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">
                                  {d}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No domains captured</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Sample Products</p>
                        {summary.samples.length === 0 ? (
                          <p className="text-sm text-gray-400">No product items returned. Try refresh or confirm the site is ecommerce.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {summary.samples.map((item, idx) => (
                              <div key={`${item.title}-${idx}`} className="flex gap-3 bg-gray-800/60 rounded-lg p-3">
                                <div className="w-16 h-16 rounded bg-gray-700/60 flex items-center justify-center overflow-hidden">
                                  {item.image ? (
                                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-xs text-gray-400">No image</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm text-white font-medium line-clamp-2">{item.title}</p>
                                  <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-2">
                                    {item.price && <span>${item.price}</span>}
                                    {item.rating && <span>Rating {item.rating}</span>}
                                    {item.reviews && <span>{item.reviews} reviews</span>}
                                    {item.availability && <span>{item.availability}</span>}
                                  </div>
                                  {item.url && (
                                    <a
                                      className="text-xs text-emerald-300 hover:text-emerald-200 mt-1 inline-block"
                                      href={item.url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      View product
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'linkedin' && payload) {
                  const summary = summarizeLinkedinData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-sky-900/40 to-gray-900 border border-sky-700/40 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-lg bg-gray-800/80 flex items-center justify-center overflow-hidden">
                            {summary.logo ? (
                              <img src={summary.logo} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-gray-400">Logo</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-sky-300/80">LinkedIn Company</p>
                            <h4 className="text-lg font-semibold text-white">
                              {summary.name || dataDrawer.companyName}
                            </h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.description || 'No description found.'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {summary.employeeCount !== undefined && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-sky-600 text-white">
                                {summary.employeeCount} employees
                              </span>
                            )}
                            {summary.size && (
                              <span className="text-xs text-sky-200">{summary.size}</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Industry</span>
                            <div>{summary.industry || 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Headquarters</span>
                            <div>{summary.headquarters || 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Website</span>
                            <div>{summary.website || 'Unknown'}</div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Specialties</p>
                          <div className="flex flex-wrap gap-2">
                            {summary.specialties.length ? (
                              summary.specialties.map((sp) => (
                                <span key={sp} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">
                                  {sp}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No specialties listed</span>
                            )}
                          </div>
                        </div>
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                          <p className="text-xs uppercase text-gray-500 mb-2">Similar Pages</p>
                          <div className="space-y-2">
                            {summary.similar.length ? (
                              summary.similar.map((s) => (
                                <div key={s.name} className="text-xs text-gray-300">
                                  {s.link ? (
                                    <a className="text-sky-300 hover:text-sky-200" href={s.link} target="_blank" rel="noreferrer">
                                      {s.name}
                                    </a>
                                  ) : (
                                    s.name
                                  )}
                                </div>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">No similar pages</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                        {summary.posts.length === 0 ? (
                          <p className="text-sm text-gray-400">No posts captured.</p>
                        ) : (
                          <div className="space-y-3">
                            {summary.posts.map((post, idx) => (
                              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                <div className="text-xs text-gray-400">
                                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                </div>
                                <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                  {post.text || 'Post content unavailable'}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                  {post.likes !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                  )}
                                  {post.comments !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                  )}
                                  {post.shares !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>
                                  )}
                                  {post.views !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                  )}
                                </div>
                                {post.url && (
                                  <a className="text-xs text-sky-300 hover:text-sky-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                    View post
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Featured Employees</p>
                        {summary.employees.length === 0 ? (
                          <p className="text-sm text-gray-400">No employees captured.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {summary.employees.map((emp) => (
                              <div key={emp.name} className="flex items-center gap-3 bg-gray-800/60 rounded-lg p-3">
                                <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden">
                                  {emp.image ? <img src={emp.image} alt="" className="w-full h-full object-cover" /> : null}
                                </div>
                                <div className="text-sm text-gray-200">
                                  {emp.link ? (
                                    <a className="text-sky-300 hover:text-sky-200" href={emp.link} target="_blank" rel="noreferrer">
                                      {emp.name}
                                    </a>
                                  ) : (
                                    emp.name
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'facebook' && payload) {
                  const summary = summarizeFacebookData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-indigo-900/40 to-gray-900 border border-indigo-700/40 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                            {summary.profilePictureUrl ? (
                              <img src={summary.profilePictureUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-gray-400">Logo</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-indigo-300/80">Facebook Page</p>
                            <h4 className="text-lg font-semibold text-white">
                              {summary.title || dataDrawer.companyName}
                            </h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.intro || 'No intro found.'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {summary.followers !== undefined && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-600 text-white">
                                {summary.followers} followers
                              </span>
                            )}
                            {summary.likes !== undefined && (
                              <span className="text-xs text-indigo-200">{summary.likes} likes</span>
                            )}
                          </div>
                        </div>
                        {summary.coverPhotoUrl && (
                          <div className="mt-4 rounded-lg overflow-hidden border border-gray-800">
                            <img src={summary.coverPhotoUrl} alt="" className="w-full h-28 object-cover" />
                          </div>
                        )}
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Category</span>
                            <div>{summary.category || 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Phone</span>
                            <div>{summary.phone || 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Website</span>
                            <div>{summary.website || 'Unknown'}</div>
                          </div>
                        </div>
                        {summary.address && (
                          <div className="mt-3 text-xs text-gray-400">Address: {summary.address}</div>
                        )}
                        {summary.pageUrl && (
                          <a className="text-xs text-indigo-300 hover:text-indigo-200 mt-2 inline-block" href={summary.pageUrl} target="_blank" rel="noreferrer">
                            View page
                          </a>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                        {summary.posts.length === 0 ? (
                          <p className="text-sm text-gray-400">No posts captured.</p>
                        ) : (
                          <div className="space-y-3">
                            {summary.posts.map((post, idx) => (
                              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                <div className="text-xs text-gray-400">
                                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                </div>
                                <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                  {post.text || 'Post content unavailable'}
                                </div>
                                {(post.image || post.video) && (
                                  <div className="mt-2 text-xs text-indigo-200">
                                    {post.video ? 'Video post' : 'Image post'}
                                  </div>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                  {post.likes !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                  )}
                                  {post.comments !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                  )}
                                  {post.shares !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>
                                  )}
                                  {post.views !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                  )}
                                </div>
                                {post.url && (
                                  <a className="text-xs text-indigo-300 hover:text-indigo-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                    View post
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'instagram' && payload) {
                  const summary = summarizeInstagramData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-fuchsia-900/40 to-gray-900 border border-fuchsia-700/40 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                            {summary.avatar ? (
                              <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-gray-400">Avatar</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-fuchsia-300/80">Instagram Profile</p>
                            <h4 className="text-lg font-semibold text-white">
                              {summary.fullName || dataDrawer.companyName}
                            </h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.bio || 'No bio found.'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {summary.isVerified && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-fuchsia-600 text-white">
                                Verified
                              </span>
                            )}
                            {summary.username && (
                              <span className="text-xs text-fuchsia-200">@{summary.username}</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Followers</span>
                            <div>{summary.followers ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Following</span>
                            <div>{summary.following ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Posts</span>
                            <div>{summary.postsCount ?? 'Unknown'}</div>
                          </div>
                        </div>
                        {summary.website && (
                          <div className="mt-3 text-xs text-gray-400">Website: {summary.website}</div>
                        )}
                        {summary.profileUrl && (
                          <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
                            View profile
                          </a>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                        {summary.recentPosts.length === 0 ? (
                          <p className="text-sm text-gray-400">No posts captured.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {summary.recentPosts.map((post, idx) => (
                              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                <div className="text-xs text-gray-400">
                                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                </div>
                                <div className="text-sm text-gray-200 mt-1">
                                  {post.caption || 'Post content unavailable'}
                                </div>
                                {(post.image || post.video) && (
                                  <div className="mt-2 space-y-2">
                                    {post.image && (
                                      <img src={post.image} alt="" className="w-full rounded-md border border-gray-700" />
                                    )}
                                    {post.video && (
                                      <video
                                        src={post.video}
                                        controls
                                        preload="metadata"
                                        className="w-full rounded-md border border-gray-700"
                                      />
                                    )}
                                  </div>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                  {post.likes !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                  )}
                                  {post.comments !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                  )}
                                </div>
                                {post.url && (
                                  <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                    View post
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'tiktok' && payload) {
                  const summary = summarizeTikTokData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-cyan-900/40 to-gray-900 border border-cyan-700/40 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                            {summary.avatar ? (
                              <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-gray-400">Avatar</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-cyan-300/80">TikTok Profile</p>
                            <h4 className="text-lg font-semibold text-white">
                              {summary.fullName || dataDrawer.companyName}
                            </h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.bio || 'No bio found.'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {summary.isVerified && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-cyan-600 text-white">
                                Verified
                              </span>
                            )}
                            {summary.username && (
                              <span className="text-xs text-cyan-200">@{summary.username}</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-300">
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Followers</span>
                            <div>{summary.followers ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Following</span>
                            <div>{summary.following ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Videos</span>
                            <div>{summary.videos ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Hearts</span>
                            <div>{summary.hearts ?? 'Unknown'}</div>
                          </div>
                        </div>
                        {summary.website && (
                          <div className="mt-3 text-xs text-gray-400">Website: {summary.website}</div>
                        )}
                        {summary.profileUrl && (
                          <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
                            View profile
                          </a>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                        {summary.recentPosts.length === 0 ? (
                          <p className="text-sm text-gray-400">No posts captured.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {summary.recentPosts.map((post, idx) => (
                              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                <div className="text-xs text-gray-400">
                                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                </div>
                                <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                  {post.caption || 'Post content unavailable'}
                                </div>
                                {(post.image || post.duration) && (
                                  <div className="mt-2 text-xs text-cyan-200">
                                    {post.duration ? `Video (${post.duration}s)` : 'Image post'}
                                  </div>
                                )}
                                {post.hashtags && post.hashtags.length > 0 && (
                                  <div className="mt-2 text-[11px] text-cyan-200">
                                    {post.hashtags.slice(0, 4).map((tag) => `#${tag}`).join(' ')}
                                  </div>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                  {post.likes !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                  )}
                                  {post.comments !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                  )}
                                  {post.shares !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>
                                  )}
                                  {post.views !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                  )}
                                </div>
                                {post.url && (
                                  <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                    View post
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'podcast' && payload) {
                  const summary = summarizePodcastData(payload);
                  const latest = summary.latestEpisode;
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-fuchsia-900/40 to-gray-900 border border-fuchsia-700/40 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                            {summary.image ? (
                              <img src={summary.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-gray-400">Artwork</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-fuchsia-300/80">Podcast</p>
                            <h4 className="text-lg font-semibold text-white">
                              {summary.showTitle || dataDrawer.companyName}
                            </h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.publisher || 'Publisher unknown'}
                            </p>
                            {summary.description && (
                              <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                                {stripHtml(summary.description)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-300">
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Website</span>
                            <div>{summary.website ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">RSS</span>
                            <div>{summary.rssUrl ?? 'Unknown'}</div>
                          </div>
                        </div>
                        {summary.website && (
                          <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={summary.website} target="_blank" rel="noreferrer">
                            Visit website
                          </a>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Latest Episode</p>
                        {!latest ? (
                          <p className="text-sm text-gray-400">No episode captured.</p>
                        ) : (
                          <div className="bg-gray-800/60 rounded-lg p-3">
                            <div className="text-sm text-gray-200 font-semibold">
                              {latest.title || 'Episode'}
                            </div>
                            {latest.duration && (
                              <div className="text-xs text-gray-400 mt-1">Duration: {latest.duration}</div>
                            )}
                            {latest.description && (
                              <div className="text-xs text-gray-300 mt-2 line-clamp-3">
                                {stripHtml(latest.description)}
                              </div>
                            )}
                            {latest.episodeUrl && (
                              <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={latest.episodeUrl} target="_blank" rel="noreferrer">
                                Listen
                              </a>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Episodes</p>
                        {summary.episodes.length === 0 ? (
                          <p className="text-sm text-gray-400">No episodes captured.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {summary.episodes.slice(0, 10).map((ep, idx) => (
                              <div key={`${ep.episodeUrl}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                <div className="text-sm text-gray-200 line-clamp-2">
                                  {ep.title || 'Episode'}
                                </div>
                                {ep.duration && (
                                  <div className="text-xs text-gray-400 mt-1">Duration: {ep.duration}</div>
                                )}
                                {ep.description && (
                                  <div className="text-xs text-gray-300 mt-2 line-clamp-2">
                                    {stripHtml(ep.description)}
                                  </div>
                                )}
                                {ep.episodeUrl && (
                                  <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={ep.episodeUrl} target="_blank" rel="noreferrer">
                                    Listen
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'youtube' && payload) {
                  const summary = summarizeYouTubeData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-red-900/40 to-gray-900 border border-red-700/40 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                            {summary.channelAvatar ? (
                              <img src={summary.channelAvatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-gray-400">Avatar</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-red-300/80">YouTube Channel</p>
                            <h4 className="text-lg font-semibold text-white">
                              {summary.channelName || dataDrawer.companyName}
                            </h4>
                            <p className="text-sm text-gray-400 mt-1">
                              {summary.channelLocation || 'Location unknown'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Subscribers</span>
                            <div>{summary.subscribers ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Total Views</span>
                            <div>{summary.totalViews ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Videos</span>
                            <div>{summary.totalVideos ?? 'Unknown'}</div>
                          </div>
                        </div>
                        {summary.channelUrl && (
                          <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={summary.channelUrl} target="_blank" rel="noreferrer">
                            View channel
                          </a>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Recent Videos</p>
                        {summary.recentVideos.length === 0 ? (
                          <p className="text-sm text-gray-400">No videos captured.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {summary.recentVideos.map((video, idx) => (
                              <div key={`${video.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                <div className="text-xs text-gray-400">
                                  {video.date ? new Date(video.date).toLocaleDateString() : 'No date'}
                                </div>
                                <div className="text-sm text-gray-200 mt-1">
                                  {video.title || video.text || 'Video'}
                                </div>
                                {video.thumbnail && (
                                  <div className="mt-2">
                                    <img src={video.thumbnail} alt="" className="w-full rounded-md border border-gray-700" />
                                  </div>
                                )}
                                {video.text && (
                                  <div className="mt-2 text-xs text-gray-300">{video.text}</div>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                  {video.views !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {video.views}</span>
                                  )}
                                  {video.likes !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {video.likes}</span>
                                  )}
                                  {video.comments !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {video.comments}</span>
                                  )}
                                </div>
                                {video.url && (
                                  <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={video.url} target="_blank" rel="noreferrer">
                                    View video
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                if (dataDrawer.type === 'x' && payload) {
                  const summary = summarizeXData(payload);
                  return (
                    <div className="space-y-5">
                      <div className="bg-gradient-to-br from-slate-800/60 to-gray-900 border border-slate-600/40 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                            {summary.avatar ? (
                              <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-gray-400">Avatar</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-slate-300/80">X Profile</p>
                            <h4 className="text-lg font-semibold text-white">
                              {summary.profileName || dataDrawer.companyName}
                            </h4>
                            {summary.profileHandle && (
                              <p className="text-sm text-gray-400 mt-1">@{summary.profileHandle}</p>
                            )}
                            {summary.bio && (
                              <p className="text-sm text-gray-300 mt-2">{summary.bio}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-300">
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Followers</span>
                            <div>{summary.followers ?? 'Unknown'}</div>
                          </div>
                          <div className="bg-gray-900/60 rounded-lg p-2">
                            <span className="text-gray-500">Following</span>
                            <div>{summary.following ?? 'Unknown'}</div>
                          </div>
                        </div>
                        {summary.profileUrl && (
                          <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
                            View profile
                          </a>
                        )}
                      </div>

                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                        {summary.recentPosts.length === 0 ? (
                          <p className="text-sm text-gray-400">No posts captured.</p>
                        ) : (
                          <div className="space-y-3">
                            {summary.recentPosts.map((post, idx) => (
                              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                <div className="text-xs text-gray-400">
                                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                </div>
                                {post.media && (
                                  <div className="mt-2">
                                    <img src={post.media} alt="" className="w-full max-w-sm rounded-md border border-gray-700" />
                                  </div>
                                )}
                                <div className="text-sm text-gray-200 mt-1">
                                  {post.text || 'Post content unavailable'}
                                </div>
                                {Array.isArray(post.repliesList) && post.repliesList.length > 0 && (
                                  <details className="mt-2 text-xs text-gray-300">
                                    <summary className="cursor-pointer text-gray-400">Replies ({post.repliesList.length})</summary>
                                    <div className="mt-2 space-y-2">
                                      {post.repliesList.slice(0, 5).map((reply, rIdx) => {
                                        const replyText =
                                          reply.text ||
                                          reply.full_text ||
                                          reply.postText ||
                                          reply.comment ||
                                          reply.content ||
                                          '';
                                        const replyAuthor =
                                          reply.author?.name ||
                                          reply.user?.name ||
                                          reply.username ||
                                          reply.authorName ||
                                          '';
                                        return (
                                          <div key={`${post.url}-reply-${rIdx}`} className="bg-gray-900/60 rounded p-2">
                                            {replyAuthor && <div className="text-gray-400 text-[11px]">{replyAuthor}</div>}
                                            <div className="text-gray-200 text-xs">{replyText || 'Reply content unavailable'}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </details>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                  {post.likes !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                  )}
                                  {post.replies !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Replies {post.replies}</span>
                                  )}
                                  {post.reposts !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Reposts {post.reposts}</span>
                                  )}
                                  {post.quotes !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Quotes {post.quotes}</span>
                                  )}
                                  {post.views !== undefined && (
                                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                  )}
                                </div>
                                {post.url && (
                                  <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                    View post
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowRawDrawer((v) => !v)}
                        className="text-xs text-gray-400 hover:text-gray-200 underline"
                      >
                        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
                      </button>
                      {showRawDrawer && (
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-xs text-gray-300">
                            {JSON.stringify(payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs text-gray-300">
                      {JSON.stringify(
                        (() => {
                          if (dataDrawer.type === 'social_grade') {
                            const intel = ecomIntelData[dataDrawer.companyId] || {};
                            const coverage = getSocialCoverage(intel);
                            const grade = getSocialGrade(coverage.found);
                            return {
                              social_grade: grade,
                              profiles_found: coverage.found,
                              profiles_total: coverage.total,
                              sources: {
                                linkedin: Boolean(
                                  intel.linkedin?.data &&
                                    (!Array.isArray(intel.linkedin.data) ||
                                      intel.linkedin.data.length > 0)
                                ),
                                facebook: Boolean(
                                  intel.facebook?.data &&
                                    (!Array.isArray(intel.facebook.data) ||
                                      intel.facebook.data.length > 0)
                                ),
                                instagram: Boolean(
                                  intel.instagram?.data &&
                                    (!Array.isArray(intel.instagram.data) ||
                                      intel.instagram.data.length > 0)
                                ),
                                tiktok: Boolean(
                                  intel.tiktok?.data &&
                                    (!Array.isArray(intel.tiktok.data) ||
                                      intel.tiktok.data.length > 0)
                                ),
                                youtube: Boolean(
                                  intel.youtube?.data &&
                                    (!Array.isArray(intel.youtube.data) ||
                                      intel.youtube.data.length > 0)
                                ),
                                x: Boolean(
                                  intel.x?.data &&
                                    (!Array.isArray(intel.x.data) ||
                                      intel.x.data.length > 0)
                                ),
                                podcast: Boolean(
                                  intel.podcast?.data &&
                                    (!Array.isArray(intel.podcast.data) ||
                                      intel.podcast.data.length > 0)
                                ),
                              },
                            };
                          }
                          return payload;
                        })(),
                        null,
                        2
                      )}
                    </pre>
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Add Company Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Add Company</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Company Name</label>
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={e => setNewCompanyName(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  placeholder="Acme Inc"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Website</label>
                <input
                  type="text"
                  value={newCompanyWebsite}
                  onChange={e => setNewCompanyWebsite(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  placeholder="acme.com"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setAddModal(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold text-sm">Cancel</button>
                <button onClick={handleAddCompany} className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm">Add Company</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">E-commerce Intelligence</h2>
            <p className="text-sm text-gray-400 mt-1">Website and social media insights for e-commerce prospects</p>
          </div>
          <div className="text-sm text-gray-400">
            Companies: <span className="text-white font-bold">{filteredCompanies.length}</span>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-card-dark border-b border-gray-800 px-6 py-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search company name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Filter:</label>
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            >
              <option value="all">All Companies</option>
              <option value="watched">Watched Only</option>
              <option value="graded">Graded Only</option>
              <option value="scraped">Has Data</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Show:</label>
            <select
              value={perPage}
              onChange={e => setPerPage(Number(e.target.value))}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>
          
          <button
            onClick={() => setAddModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Company
          </button>
          
          <button
            onClick={() => {
              void loadData();
            }}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button
            onClick={handleExportCSV}
            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Grade Legend */}
      <div className="px-6 py-2 bg-gray-900 border-b border-gray-800">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="text-gray-400">Grades:</span>
          <span><span className="px-2 py-0.5 rounded font-bold bg-green-500 text-white">A</span> Excellent opportunity</span>
          <span><span className="px-2 py-0.5 rounded font-bold bg-lime-500 text-white">B</span> Good potential</span>
          <span><span className="px-2 py-0.5 rounded font-bold bg-yellow-500 text-black">C</span> Average fit</span>
          <span><span className="px-2 py-0.5 rounded font-bold bg-orange-500 text-white">D</span> Limited potential</span>
          <span><span className="px-2 py-0.5 rounded font-bold bg-red-500 text-white">F</span> Poor fit</span>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/75 z-40 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500 mx-auto mb-4" />
            <p className="text-white text-lg">Loading companies...</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="px-6 py-4">
        <div className="bg-card-dark border border-gray-700 rounded-xl overflow-hidden">
          <div className="max-h-[calc(100vh-340px)] overflow-y-auto overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-700">
                  <th className="px-2 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Watch</th>
                  <th className="px-2 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Company</th>
                  <th className="px-2 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Website</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-blue-300 uppercase bg-card-dark" title="Website content">Site</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-sky-300 uppercase bg-card-dark" title="LinkedIn profile">LI</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-indigo-300 uppercase bg-card-dark" title="Facebook page">FB</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-pink-300 uppercase bg-card-dark" title="Instagram profile">IG</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-cyan-300 uppercase bg-card-dark" title="TikTok profile">TikTok</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-red-300 uppercase bg-card-dark" title="YouTube channel">YT</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-gray-300 uppercase bg-card-dark" title="X / Twitter profile">X</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-fuchsia-300 uppercase bg-card-dark" title="Podcast search">Podcast</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-emerald-300 uppercase bg-card-dark" title="Found social profiles">Social Grade</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-emerald-300 uppercase bg-card-dark" title="E-commerce quality">EcomIQ</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-amber-300 uppercase bg-card-dark" title="Marketing effectiveness">MarketingIQ</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-violet-300 uppercase bg-card-dark" title="Ad platform readiness">AdReadyIQ</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-rose-300 uppercase bg-card-dark" title="Affiliate potential">AffiliateIQ</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-yellow-300 uppercase bg-card-dark" title="Product extraction">ProductIQ</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-teal-300 uppercase bg-card-dark" title="Service opportunities">EcomMap</th>
                  <th className="px-2 py-3 text-center text-xs font-semibold text-pink-300 uppercase bg-card-dark" title="Push to Make.com">Push</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={19} className="px-6 py-12 text-center text-gray-500">No companies found</td>
                  </tr>
                ) : (
                  paginatedCompanies.map(company => {
                    const intel = ecomIntelData[company.companyId] || {};
                    const websiteData = intel.website?.data;
                    const ecomData = intel.ecommerce?.data;
                    const socialLinks = intel.website?.socialLinks || intel.social_links || {};
                    const socialCoverage = getSocialCoverage(intel);
                    const socialGrade = getSocialGrade(socialCoverage.found);
                    
                    const ScrapeBtn = ({ source, color, identifier }: { source: string; color: string; identifier?: string }) => {
                      const intelData = intel[source as keyof EcomIntel];
                      const data = intelData && 'data' in intelData ? intelData.data : undefined;
                      const isLoading = isActionLoading(company.companyId, source);
                      const hasRealData = data && !isMockData(data);
                      
                      if (isLoading) return <Loader2 className="w-4 h-4 animate-spin mx-auto" />;
                      if (hasRealData) {
                        const displayLabel = source === 'ecommerce' && Array.isArray(data) ? data.length.toString() : '✓';
                        return (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setDataDrawer({ companyId: company.companyId, companyName: company.companyName, type: source })}
                              className="px-2 py-1 rounded text-xs font-medium bg-green-600 text-white"
                              title="View data"
                            >
                              {displayLabel}
                            </button>
                            <button
                              onClick={() => {
                                if (source === 'website') return handleScrapeWebsite(company, { forceRefresh: true });
                                if (source === 'ecommerce') return handleScrapeEcommerce(company, { forceRefresh: true });
                                return handleScrapeSocial(company, source, identifier || '', { forceRefresh: true });
                              }}
                              className="p-1 rounded bg-gray-700 text-white hover:bg-gray-600"
                              title="Rerun scrape"
                            >
                              <RefreshCw className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      }
                      
                      if (source === 'website' || source === 'ecommerce') {
                        return (
                          <button
                            onClick={() => source === 'website' ? handleScrapeWebsite(company) : handleScrapeEcommerce(company)}
                            className="px-2 py-1 rounded text-xs font-medium text-white"
                            style={{ background: color }}
                          >
                            {CREDIT_COSTS[source]}cr
                          </button>
                        );
                      }
                      
                      // Social buttons: allow scraping even without URL (backend can auto-discover).
                      const hasUrl = Boolean(identifier);
                      return (
                        <button
                          onClick={() => handleScrapeSocial(company, source, identifier || '')}
                          className="px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ background: color }}
                          title={hasUrl ? 'URL discovered from website' : 'No URL found yet'}
                        >
                          <span className="flex items-center gap-1">
                            {CREDIT_COSTS[source]}cr
                            {hasUrl && <Link className="w-3 h-3" />}
                          </span>
                        </button>
                      );
                    };
                    
                    const GradeBtn = ({ gradeType, color }: { gradeType: string; color: string }) => {
                      const gradeIntel = intel[gradeType as keyof EcomIntel];
                      const data = gradeIntel && 'data' in gradeIntel ? gradeIntel.data as { overall_grade?: string; total_products_found?: number; best_opportunity?: string } : undefined;
                      const isLoading = isActionLoading(company.companyId, gradeType);
                      const hasData = websiteData || ecomData;
                      
                      if (isLoading) return <Loader2 className="w-4 h-4 animate-spin mx-auto" />;
                      if (data) {
                        let label;
                        if (gradeType === 'ecommap') label = data.best_opportunity?.substring(0, 6) || '✓';
                        else if (gradeType === 'productiq') label = data.total_products_found?.toString() || '✓';
                        else label = data.overall_grade || '✓';
                        return (
                          <button
                            onClick={() => setDataDrawer({ companyId: company.companyId, companyName: company.companyName, type: gradeType })}
                            className={`px-2 py-1 rounded text-xs font-bold ${getGradeColor(data.overall_grade)} text-white`}
                          >
                            {label}
                          </button>
                        );
                      }
                      if (!hasData) return <span className="text-gray-600">-</span>;
                      return (
                        <button
                          onClick={() => handleGrade(company, gradeType)}
                          className="px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ background: color }}
                        >
                          Grade
                        </button>
                      );
                    };
                    
                    return (
                      <tr key={company.companyId} className="border-b border-gray-800 hover:bg-gray-900/50">
                        <td className="px-2 py-3 text-center">
                          <button onClick={() => toggleWatch(company.companyId)}>
                            {watchList[company.companyId] ? (
                              <Eye className="w-4 h-4 text-yellow-500" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                        </td>
                        <td className="px-2 py-3">
                          <div className="font-semibold text-white">{company.companyName}</div>
                        </td>
                        <td className="px-2 py-3">
                          <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                            {company.website} <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="website" color="linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)" />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="linkedin" color="linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)" identifier={socialLinks.linkedin} />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="facebook" color="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" identifier={socialLinks.facebook} />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="instagram" color="linear-gradient(135deg, #ec4899 0%, #be185d 100%)" identifier={socialLinks.instagram} />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="tiktok" color="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)" identifier={socialLinks.tiktok} />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="youtube" color="linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)" identifier={socialLinks.youtube} />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="x" color="linear-gradient(135deg, #4b5563 0%, #111827 100%)" identifier={socialLinks.x || socialLinks.twitter} />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <ScrapeBtn source="podcast" color="linear-gradient(135deg, #d946ef 0%, #a21caf 100%)" identifier={company.companyName} />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <button
                            onClick={() => setDataDrawer({ companyId: company.companyId, companyName: company.companyName, type: 'social_grade' })}
                            className={`px-2 py-1 rounded text-xs font-bold ${getGradeColor(socialGrade)} text-white`}
                            title={`${socialCoverage.found}/7 profiles found`}
                          >
                            {socialGrade}
                          </button>
                        </td>
                        <td className="px-2 py-3 text-center">
                          <GradeBtn gradeType="ecomiq" color="linear-gradient(135deg, #10b981 0%, #047857 100%)" />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <GradeBtn gradeType="marketingiq" color="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <GradeBtn gradeType="adreadyiq" color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <GradeBtn gradeType="affiliateiq" color="linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)" />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <GradeBtn gradeType="productiq" color="linear-gradient(135deg, #eab308 0%, #ca8a04 100%)" />
                        </td>
                        <td className="px-2 py-3 text-center">
                          <GradeBtn gradeType="ecommap" color="linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" />
                        </td>
                        <td className="px-2 py-3 text-center">
                          {isActionLoading(company.companyId, 'push') ? (
                            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                          ) : (
                            <button
                              onClick={() => handlePush(company)}
                              className="px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}
                            >
                              <Send className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 flex items-center justify-between">
        <div className="text-sm text-gray-400">
          Showing {filteredCompanies.length > 0 ? startIdx + 1 : 0}-{endIdx} of {filteredCompanies.length}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-50"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-400">Page {currentPage} of {totalPages || 1}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-50"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-4 mt-8">
        <div className="flex justify-between items-center text-sm text-gray-500">
          <div>© 2026 Profitibull. All rights reserved.</div>
          <div className="flex space-x-4">
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
